import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faCheckSquare,
  faFilePdf,
  faFilter,
  faRotate,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../../../components/Ui_Element/CustomToast";
import config from "../../../../config";
import { getlist } from "../../../../contexts/apicontexts";
import { getCurrentUserIdentity } from "../../ticketUtils";
import {
  fetchOrganizationCustomerTicketSummary,
  fetchTicketReportStatusOptions,
} from "./ticketSummaryReportService";
import { fetchTicketTaskDetailsReport } from "./ticketTaskDetailsReportService";
import { openTicketTaskDetailsPdfReport } from "./ticketTaskDetailsPdfService";
import { openTicketSummaryPdfReport } from "./ticketSummaryPdfService";

const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().split("T")[0];
};

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};

const normalizeOrganization = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  organizationCode: String(item?.organizationCode ?? item?.OrganizationCode ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  primaryEmail: String(item?.primaryEmail ?? item?.PrimaryEmail ?? "").trim(),
  website: String(item?.website ?? item?.Website ?? "").trim(),
});

const normalizeCustomer = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? "").trim(),
  organizationId: Number(item?.organizationId ?? item?.OrganizationId ?? 0),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  mobile: String(item?.mobile ?? item?.Mobile ?? "").trim(),
  address: String(item?.address ?? item?.Address ?? "").trim(),
  domain: String(item?.domain ?? item?.Domain ?? "").trim(),
  empUserId: String(item?.empUserId ?? item?.EmpUserId ?? "").trim(),
});

const normalizeCustomerUser = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  customerId: Number(item?.customerId ?? item?.CustomerId ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  email: String(item?.email ?? item?.Email ?? "").trim(),
  customerName: String(item?.customerName ?? item?.CustomerName ?? "").trim(),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? "").trim(),
});

const normalizeEmployee = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? item?.userID ?? item?.UserID ?? "").trim(),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? "").trim(),
  employeeCode: String(item?.employeeCode ?? item?.EmployeeCode ?? "").trim(),
  employeeNumber: String(item?.employeeNumber ?? item?.EmployeeNumber ?? "").trim(),
  workEmail: String(item?.workEmail ?? item?.WorkEmail ?? item?.email ?? item?.Email ?? "").trim(),
});

const getIdentityParts = (nameValue, emailValue, fallback) => ({
  name: String(nameValue ?? "").trim() || fallback,
  email: String(emailValue ?? "").trim(),
});

const formatDateOnly = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const emptyReport = {
  items: [],
  totalTickets: 0,
  totalOrganizations: 0,
  totalCustomers: 0,
};

const COLUMN_OPTIONS = [
  { key: "ticketName", label: "Ticket Name" },
  { key: "organization", label: "Organization" },
  { key: "employee", label: "Employee" },
  { key: "customer", label: "Customer" },
  { key: "customerUser", label: "Customer User" },
  { key: "openDate", label: "Open Date" },
  { key: "status", label: "Status" },
  { key: "taskCount", label: "Task Count" },
];

const DEFAULT_SELECTED_COLUMNS = COLUMN_OPTIONS.map((item) => item.key);

function ReportField({ label, required = false, children, helper }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? <div className="mt-2 text-xs text-slate-500">{helper}</div> : null}
    </div>
  );
}

function ColumnMultiSelect({
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  onUnselectAll,
  isOpen,
  onOpenChange,
}) {
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.key))
    .map((option) => option.label)
    .join(", ");

  useEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const estimatedMenuHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const shouldOpenUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
      setMenuStyle({
        position: "fixed",
        top: shouldOpenUp ? "auto" : rect.bottom + 8,
        bottom: shouldOpenUp ? window.innerHeight - rect.top + 8 : "auto",
        left: Math.max(8, rect.right - width),
        width,
        zIndex: 9999,
      });
    };

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative z-10">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 transition hover:border-slate-300"
      >
        <span className="min-w-0 flex-1 truncate text-left">
          {selectedLabels || "Select columns"}
        </span>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {selectedValues.length} selected
        </span>
      </button>

      {isOpen && menuStyle ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 px-2 pb-2">
            <button
              type="button"
              onClick={onSelectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={onUnselectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Unselect All
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain">
            {options.map((option) => {
              const checked = selectedValues.includes(option.key);

              return (
                <label
                  key={option.key}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(option.key)}
                    disabled={selectedValues.length === 1 && checked}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  />
                  <span className="min-w-0 flex-1 break-words">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

export default function OrganizationCustomerSummaryReport() {
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusOptions, setStatusOptions] = useState(["All"]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [customerUsersLoading, setCustomerUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskDetailsLoadingTicketId, setTaskDetailsLoadingTicketId] = useState(0);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(emptyReport);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    organizationId: "",
    employeeUserId: "",
    customerId: "",
    customerUserId: "",
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    status: "All",
  });

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setFiltersLoading(true);
      setError("");

      try {
        const [
          organizationResult,
          customerResult,
          employeeResult,
          statusResult,
        ] = await Promise.allSettled([
          getlist(`${config.baseurl}Organizations/getlist`),
          getlist(`${config.baseurl}Customer/getlist`),
          getlist(`${config.baseurl}Employee/getlist`),
          fetchTicketReportStatusOptions(),
        ]);

        if (cancelled) return;

        const failedSources = [];

        if (organizationResult.status === "fulfilled") {
          setOrganizations(Array.isArray(organizationResult.value?.data) ? organizationResult.value.data.map(normalizeOrganization) : []);
        } else {
          failedSources.push("Organizations");
          setOrganizations([]);
        }

        if (customerResult.status === "fulfilled") {
          setCustomers(Array.isArray(customerResult.value?.data) ? customerResult.value.data.map(normalizeCustomer) : []);
        } else {
          failedSources.push("Customers");
          setCustomers([]);
        }

        if (employeeResult.status === "fulfilled") {
          setEmployees(Array.isArray(employeeResult.value?.data) ? employeeResult.value.data.map(normalizeEmployee) : []);
        } else {
          failedSources.push("Employees");
          setEmployees([]);
        }

        if (statusResult.status === "fulfilled") {
          setStatusOptions(["All", ...statusResult.value]);
        } else {
          failedSources.push("Status Options");
          setStatusOptions(["All"]);
        }

        if (failedSources.length > 0) {
          setError(`Report filters could not be fully loaded. Failed: ${failedSources.join(", ")}.`);
        }
      } catch {
        if (!cancelled) {
          setError("Report filters could not be loaded.");
        }
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOrganization = useMemo(
    () => organizations.find((item) => Number(item.id) === Number(filters.organizationId || 0)) || null,
    [filters.organizationId, organizations]
  );

  const visibleCustomers = useMemo(() => {
    if (!filters.organizationId) return customers;
    return customers.filter((item) => Number(item.organizationId || 0) === Number(filters.organizationId));
  }, [customers, filters.organizationId]);

  const selectedEmployee = useMemo(
    () => employees.find((item) => item.userId === filters.employeeUserId) || null,
    [employees, filters.employeeUserId]
  );

  const visibleCustomersByEmployee = useMemo(() => {
    if (!filters.employeeUserId) return visibleCustomers;
    return visibleCustomers.filter((item) => String(item.empUserId || "").trim() === filters.employeeUserId);
  }, [filters.employeeUserId, visibleCustomers]);

  const visibleCustomerIdSet = useMemo(
    () => new Set(visibleCustomersByEmployee.map((item) => Number(item.id))),
    [visibleCustomersByEmployee]
  );

  useEffect(() => {
    let cancelled = false;

    const loadCustomerUsers = async () => {
      const scopedCustomerIds = filters.customerId
        ? [Number(filters.customerId)]
        : filters.organizationId || filters.employeeUserId
          ? visibleCustomersByEmployee.map((item) => Number(item.id)).filter((id) => id > 0)
          : [];

      if (scopedCustomerIds.length === 0) {
        setCustomerUsers([]);
        setCustomerUsersLoading(false);
        return;
      }

      setCustomerUsersLoading(true);

      try {
        const responses = await Promise.allSettled(
          scopedCustomerIds.map((customerId) =>
            getlist(`${config.baseurl}CustomerUser/getlist?customerId=${encodeURIComponent(customerId)}`)
          )
        );

        if (cancelled) return;

        const mergedUsers = responses
          .filter((result) => result.status === "fulfilled" && Array.isArray(result.value?.data))
          .flatMap((result) => result.value.data)
          .map(normalizeCustomerUser);

        const uniqueUsers = Array.from(
          new Map(mergedUsers.map((item) => [`${item.id}-${item.userId}`, item])).values()
        );

        setCustomerUsers(uniqueUsers);
      } catch {
        if (!cancelled) {
          setCustomerUsers([]);
        }
      } finally {
        if (!cancelled) {
          setCustomerUsersLoading(false);
        }
      }
    };

    loadCustomerUsers();

    return () => {
      cancelled = true;
    };
  }, [filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomersByEmployee]);

  const visibleCustomerUsers = useMemo(() => {
    if (filters.customerId) {
      return customerUsers.filter((item) => Number(item.customerId || 0) === Number(filters.customerId));
    }

    if (filters.organizationId || filters.employeeUserId) {
      return customerUsers.filter((item) => visibleCustomerIdSet.has(Number(item.customerId || 0)));
    }

    return customerUsers;
  }, [customerUsers, filters.customerId, filters.employeeUserId, filters.organizationId, visibleCustomerIdSet]);

  const statusTotals = useMemo(() => {
    return report.items.reduce((result, item) => {
      result[item.status] = (result[item.status] || 0) + Number(item.ticketCount || 0);
      return result;
    }, {});
  }, [report.items]);

  const selectedCustomer = useMemo(
    () => customers.find((item) => Number(item.id) === Number(filters.customerId || 0)) || null,
    [customers, filters.customerId]
  );

  const selectedCustomerUser = useMemo(
    () => customerUsers.find((item) => item.userId === filters.customerUserId) || null,
    [customerUsers, filters.customerUserId]
  );

  const reportCompanyInfo = useMemo(() => {
    if (selectedOrganization) {
      return {
        name: selectedOrganization.name || "BanglaFire CRM",
        code: selectedOrganization.organizationCode || "",
        email: selectedOrganization.primaryEmail || selectedCustomer?.email || "support@banglafire.com",
        phone: selectedOrganization.mobile || selectedCustomer?.mobile || "",
        website: selectedOrganization.website || selectedCustomer?.domain || "www.banglafire.com",
        address: selectedOrganization.address || selectedCustomer?.address || "BanglaFire CRM Support Desk",
      };
    }

    if (selectedCustomer) {
      return {
        name: selectedCustomer.name || "BanglaFire CRM",
        code: selectedCustomer.customerCode || "",
        email: selectedCustomer.email || "support@banglafire.com",
        phone: selectedCustomer.mobile || "",
        website: selectedCustomer.domain || "www.banglafire.com",
        address: selectedCustomer.address || "BanglaFire CRM Support Desk",
      };
    }

    return {
      name: "BanglaFire CRM",
      code: "Support Desk",
      email: "support@banglafire.com",
      phone: "",
      website: "www.banglafire.com",
      address: "BanglaFire CRM Support Desk",
    };
  }, [selectedCustomer, selectedOrganization]);

  const reportFilterSummary = useMemo(() => ({
    organizationName: selectedOrganization?.name || "All Organizations",
    employeeName: selectedEmployee?.fullName || selectedEmployee?.workEmail || "All Employees",
    customerName: selectedCustomer?.name || "All Customers",
    customerCode: selectedCustomer?.customerCode || "",
    customerUserName: selectedCustomerUser?.fullName || selectedCustomerUser?.email || "All Customer Users",
    customerUserEmail: selectedCustomerUser?.email || "",
    startDate: filters.startDate,
    endDate: filters.endDate,
    status: filters.status === "All" ? "All Statuses" : filters.status,
  }), [filters.endDate, filters.startDate, filters.status, selectedCustomer, selectedCustomerUser, selectedEmployee, selectedOrganization]);

  useEffect(() => {
    if ((filters.organizationId || filters.employeeUserId) && !visibleCustomersByEmployee.some((item) => Number(item.id) === Number(filters.customerId || 0))) {
      setFilters((current) => ({ ...current, customerId: "", customerUserId: "" }));
      return;
    }

    if (filters.customerUserId && !visibleCustomerUsers.some((item) => item.userId === filters.customerUserId)) {
      setFilters((current) => ({ ...current, customerUserId: "" }));
    }
  }, [filters.organizationId, filters.employeeUserId, filters.customerId, filters.customerUserId, visibleCustomersByEmployee, visibleCustomerUsers]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => {
      if (field === "organizationId") {
        return {
          ...current,
          organizationId: value,
          employeeUserId: "",
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "employeeUserId") {
        return {
          ...current,
          employeeUserId: value,
          customerId: "",
          customerUserId: "",
        };
      }

      if (field === "customerId") {
        return {
          ...current,
          customerId: value,
          customerUserId: "",
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const validateFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error("Start date and end date are required.");
    }

    if (filters.endDate < filters.startDate) {
      throw new Error("End date must be on or after start date.");
    }
  };

  const handleSearch = async () => {
    try {
      validateFilters();
    } catch (validationError) {
      const message = validationError?.message || "Please review the report filters.";
      setError(message);
      showToast.error(message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchOrganizationCustomerTicketSummary({
        organizationId: filters.organizationId,
        employeeUserId: filters.employeeUserId,
        customerId: filters.customerId,
        customerUserId: filters.customerUserId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status === "All" ? "" : filters.status,
      });

      setReport(result);
      setSearched(true);
    } catch (requestError) {
      const message = requestError?.message || "Ticket summary report could not be loaded.";
      setError(message);
      setReport(emptyReport);
      setSearched(true);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      await openTicketSummaryPdfReport({
        companyInfo: reportCompanyInfo,
        filters: reportFilterSummary,
        report,
        selectedColumns,
      });
    } catch (exportError) {
      const message = exportError?.message || "PDF report could not be generated.";
      showToast.error(message);
    }
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((current) => {
      if (current.includes(columnKey)) {
        if (current.length === 1) {
          return current;
        }

        return current.filter((item) => item !== columnKey);
      }

      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
    setIsColumnMenuOpen(false);
  };

  const handleUnselectAllColumns = () => {
    setSelectedColumns([DEFAULT_SELECTED_COLUMNS[0]]);
    setIsColumnMenuOpen(false);
  };

  const isColumnVisible = (columnKey) => selectedColumns.includes(columnKey);

  const handleOpenTaskDetailsReport = async (item) => {
    const ticketId = Number(item?.ticketId || 0);
    const taskCount = Number(item?.taskCount || 0);

    if (ticketId <= 0 || taskCount <= 0) return;

    setTaskDetailsLoadingTicketId(ticketId);

    try {
      const detailReport = await fetchTicketTaskDetailsReport(ticketId);
      await openTicketTaskDetailsPdfReport({
        companyInfo: reportCompanyInfo,
        report: detailReport,
      });
    } catch (reportError) {
      const message = reportError?.message || "Task details report could not be generated.";
      showToast.error(message);
    } finally {
      setTaskDetailsLoadingTicketId(0);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Ticket Summary Report" currentPage="Ticket Summary Report" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_30%),linear-gradient(135deg,#0f172a,#1e293b_52%,#334155)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-8 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faChartColumn} />
                Support Reporting
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Organization and customer ticket summary</h1>

              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.fullName || identity.email || "Current user"}</span>
                {selectedOrganization ? (
                  <span className="rounded-full border border-amber-300/30 bg-amber-400/15 px-3 py-1 text-amber-50">
                    Organization: {selectedOrganization.name}
                  </span>
                ) : null}
                {filters.status !== "All" ? (
                  <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Status: {filters.status}</span>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={loading || filtersLoading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
              {loading ? "Loading Report..." : "Run Report"}
            </button>
          </div>
        </div>

        <div className="space-y-6 p-5 md:p-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Filters</h2>

              </div>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faFilter} />
              </div>
            </div>

            {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <ReportField label="Start Date" required>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) => handleFilterChange("startDate", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                />
              </ReportField>

              <ReportField label="End Date" required>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) => handleFilterChange("endDate", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                />
              </ReportField>

              <ReportField label="Organization">
                <select
                  value={filters.organizationId}
                  onChange={(event) => handleFilterChange("organizationId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">All Organizations</option>
                  {organizations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Employee">
                <select
                  value={filters.employeeUserId}
                  onChange={(event) => handleFilterChange("employeeUserId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">All Employees</option>
                  {employees.map((item) => (
                    <option key={item.userId || item.id} value={item.userId}>
                      {item.fullName || item.workEmail || item.userId}
                      {item.employeeCode ? ` (${item.employeeCode})` : item.employeeNumber ? ` (${item.employeeNumber})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField
                label="Customer"

              >
                <select
                  value={filters.customerId}
                  onChange={(event) => handleFilterChange("customerId", event.target.value)}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">{filters.organizationId || filters.employeeUserId ? "All Customers" : "Select Customer"}</option>
                  {visibleCustomersByEmployee.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.customerCode ? ` (${item.customerCode})` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>

              <ReportField label="Customer User">
                <select
                  value={filters.customerUserId}
                  onChange={(event) => handleFilterChange("customerUserId", event.target.value)}
                  disabled={filtersLoading || customerUsersLoading || (!filters.customerId && !filters.organizationId && !filters.employeeUserId)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400 disabled:bg-slate-50"
                >
                  <option value="">
                    {!filters.customerId && !filters.organizationId && !filters.employeeUserId
                      ? "Select customer or scope first"
                      : customerUsersLoading
                        ? "Loading customer users..."
                        : "All Customer Users"}
                  </option>
                  {visibleCustomerUsers.map((item) => (
                    <option key={`${item.id}-${item.userId}`} value={item.userId}>
                      {item.fullName || item.email || item.userId}
                      {item.customerName ? ` - ${item.customerName}` : ""}
                    </option>
                  ))}
                </select>
              </ReportField>



              <ReportField label="Status">
                <select
                  value={filters.status}
                  onChange={(event) => handleFilterChange("status", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </ReportField>

              <button
                type="button"
                onClick={() => {
                  setFilters({
                    organizationId: "",
                    employeeUserId: "",
                    customerId: "",
                    customerUserId: "",
                    startDate: getDefaultStartDate(),
                    endDate: getDefaultEndDate(),
                    status: "All",
                  });
                  setReport(emptyReport);
                  setError("");
                  setSearched(false);
                }}
                className="rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading || filtersLoading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-2 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                Search
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Status Snapshot</h2>
                <p className="mt-1 text-sm text-slate-500">Quick ticket count by status based on the current report result.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                  Refresh Result
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={loading || filtersLoading}
                  className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  PDF Report
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {(Object.keys(statusTotals).length ? Object.entries(statusTotals) : [["No Data", 0]]).map(([status, total]) => (
                <div key={status} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{status}</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">{total}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-visible rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Tickets  Rows</h2>
                <div className="flex items-center justify-end gap-2 lg:w-[280px]">
                  <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                    <FontAwesomeIcon icon={faCheckSquare} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <ColumnMultiSelect
                      options={COLUMN_OPTIONS}
                      selectedValues={selectedColumns}
                      onToggle={handleColumnToggle}
                      onSelectAll={handleSelectAllColumns}
                      onUnselectAll={handleUnselectAllColumns}
                      isOpen={isColumnMenuOpen}
                      onOpenChange={setIsColumnMenuOpen}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading report rows...
              </div>
            ) : !searched ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                Run the report to see ticket rows and task counts.
              </div>
            ) : report.items.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      {isColumnVisible("ticketName") ? <th className="px-5 py-3 font-semibold text-slate-600">Ticket Name</th> : null}
                      {isColumnVisible("organization") ? <th className="px-5 py-3 font-semibold text-slate-600">Organization</th> : null}
                      {isColumnVisible("employee") ? <th className="px-5 py-3 font-semibold text-slate-600">Employee</th> : null}
                      {isColumnVisible("customer") ? <th className="px-5 py-3 font-semibold text-slate-600">Customer</th> : null}
                      {isColumnVisible("customerUser") ? <th className="px-5 py-3 font-semibold text-slate-600">Customer User</th> : null}
                      {isColumnVisible("openDate") ? <th className="px-5 py-3 font-semibold text-slate-600">Open Date</th> : null}
                      {isColumnVisible("status") ? <th className="px-5 py-3 font-semibold text-slate-600">Status</th> : null}
                      {isColumnVisible("taskCount") ? <th className="px-5 py-3 text-right font-semibold text-slate-600">Task Count</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, index) => (
                      <tr key={item.ticketId || `${item.organizationId}-${item.customerId}-${item.customerUserId}-${item.employeeUserId}-${item.ticketName}-${item.openDate}-${item.status}-${index}`} className="border-t border-slate-100">
                        {isColumnVisible("ticketName") ? <td className="px-5 py-3 text-slate-700">{item.ticketName || "-"}</td> : null}
                        {isColumnVisible("organization") ? <td className="px-5 py-3 text-slate-700">{item.organizationName}</td> : null}
                        {isColumnVisible("employee") ? <td className="px-5 py-3 text-slate-700">
                          {(() => {
                            const employee = getIdentityParts(item.employeeName, item.employeeEmail, "Unassigned");
                            return (
                              <div className="space-y-0.5">
                                <div className="font-medium text-slate-900">{employee.name}</div>
                                {employee.email ? <div className="text-xs text-slate-500">{employee.email}</div> : null}
                              </div>
                            );
                          })()}
                        </td> : null}
                        {isColumnVisible("customer") ? <td className="px-5 py-3 font-medium text-slate-900">{item.customerName}</td> : null}
                        {isColumnVisible("customerUser") ? <td className="px-5 py-3 text-slate-700">
                          {(() => {
                            const customerUser = getIdentityParts(item.customerUserName, item.customerUserEmail, "Unknown User");
                            return (
                              <div className="space-y-0.5">
                                <div className="font-medium text-slate-900">{customerUser.name}</div>
                                {customerUser.email ? <div className="text-xs text-slate-500">{customerUser.email}</div> : null}
                              </div>
                            );
                          })()}
                        </td> : null}
                        {isColumnVisible("openDate") ? <td className="px-5 py-3 text-slate-700">{formatDateOnly(item.openDate)}</td> : null}
                        {isColumnVisible("status") ? <td className="px-5 py-3">
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                            {item.status}
                          </span>
                        </td> : null}
                        {isColumnVisible("taskCount") ? <td className="px-5 py-3 text-right font-semibold text-slate-900">
                          {Number(item.taskCount || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => handleOpenTaskDetailsReport(item)}
                              disabled={taskDetailsLoadingTicketId === Number(item.ticketId || 0)}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
                            >
                              {taskDetailsLoadingTicketId === Number(item.ticketId || 0) ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : null}
                              {item.taskCount}
                            </button>
                          ) : (
                            0
                          )}
                        </td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center text-sm text-slate-500">
                No tickets matched the selected filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
